import type { AwardDto, SourcingEventDto } from "../../../autogen/bff-api";
import { isAtOrAboveEea } from "../../sourcing-events/buyer/wizard/utils/karens";

export type ConcludedDateProblem = "BEFORE_AWARD" | "BEFORE_INTENTION_NOTICE_WAIT_ENDS" | "IN_FUTURE";

const osloDay = (instant: Date): string =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Oslo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);

export const todayInOslo = (now: Date = new Date()): string => osloDay(now);

export const awardDecisionDay = (
  award: AwardDto | null | undefined,
): string | null =>
  award?.awardedAt ? osloDay(new Date(award.awardedAt)) : null;

export const standstillLastDay = (
  award: AwardDto | null | undefined,
): string | null =>
  award?.standstillExpiresAt && !award.standstillExemption
    ? osloDay(new Date(award.standstillExpiresAt))
    : null;

export interface StandstillEnd {
  lastDay: string;
  ineffectivenessApplies: boolean;
}

export const standstillEnd = (
  event: SourcingEventDto,
): StandstillEnd | null => {
  const lastDay = standstillLastDay(event.award);
  if (lastDay == null) return null;
  const ctx = event.eventContext.type === "Public" ? event.eventContext : null;
  return {
    lastDay,
    ineffectivenessApplies:
      ctx != null && isAtOrAboveEea(ctx.regulation, ctx.foaPart),
  };
};

export const concludedDuringStandstill = (
  date: string,
  lastDayOfStandstill: string | null,
): boolean =>
  date !== "" && lastDayOfStandstill != null && date <= lastDayOfStandstill;

export const concludedDateProblem = (
  date: string,
  earliest: string | null,
  today: string = todayInOslo(),
  contractFrom: string | null = null,
): ConcludedDateProblem | null => {
  if (date === "") return null;
  if (earliest != null && date < earliest) return "BEFORE_AWARD";
  if (contractFrom != null && date < contractFrom) return "BEFORE_INTENTION_NOTICE_WAIT_ENDS";
  if (date > today) return "IN_FUTURE";
  return null;
};
